import { ReactNode, useState } from 'react';
import { faCircleInfo as faCircleInfoSolid, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo, faPen } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarOn } from '@fortawesome/sharp-solid-svg-icons';
import { faStar as faStarOff } from '@fortawesome/sharp-regular-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ScoreNumber from '~/components/ui/ScoreNumber';
import { Link } from '~/components/ui/Link';
import SlideShowButton from '~/components/ui/SlideShowButton';
import ReviewCard from '~/components/Reviews/ReviewCard';
import { Message } from '~/components/ui/Message';
import type { Review } from '~/types/Review';
import Panel from '~/components/ui/Panel';

const renderStarRating = (
  scoreDisplay: number,
  isOverall: boolean
): ReactNode => {
  const stars: ReactNode[] = [];
  const rating = Math.round(scoreDisplay / (isOverall ? 2 : 1));
  for (let i = 1; i <= 5; i += 1) {
    if (i <= rating) {
      stars.push(
        <FontAwesomeIcon 
          key={`star-${i}`}
          icon={faStarOn as IconProp} 
          className="star-rating__star--on text-2xl"
        />
      );
    } else {
      stars.push(
        <FontAwesomeIcon
          key={`star-${i}`}
          icon={faStarOff as IconProp} 
          className="star-rating__star--off text-2xl"
        />
      );
    }
  }
  return (
    <div className="star-rating">
      {stars} 
    </div>
  );
};

type ReviewBlockProps = {
  title?: string;
  scoreDisplay: number;
  reviews: Review[];
  totalReviews: number;
  name: string;
  scope?: string;
  reviewUrl: string;
  reviewIndexUrl: string;
};

const ReviewBlock: React.FC<ReviewBlockProps> = ({
  title,
  scoreDisplay,
  reviews,
  totalReviews,
  name,
  scope,
  reviewUrl,
  reviewIndexUrl,
}) => {
  const subsetSize = 3;
  const [startIndex, setStartIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const renderedReview = reviews.slice(startIndex, startIndex + subsetSize);
  const showNext = !!reviews.slice(
    startIndex + subsetSize,
    startIndex + subsetSize * 2
  ).length;

  const handleNext = () => {
    if (startIndex + subsetSize < reviews.length) {
      setStartIndex(startIndex + subsetSize);
    }
  };
  const handlePrevious = () => {
    if (startIndex - subsetSize >= 0) {
      setStartIndex(startIndex - subsetSize);
    }
  };

  const handleHover = () => {
    setIsHovered(true);
  };

  const handleLeave = () => {
    setIsHovered(false);
  };

  return (
    <Panel className="flex-col">
      <div>
        <div className={`${reviews.length > 0 ? 'pb-6 md:pb-10' : ''}`}>
          <div className="relative grid grid-cols-3 lg:grid-cols-2 gap-2.5">
            <h2 className="panel-title--primary col-start-1 row-start-1 leading-[56px] md:leading-normal">
              {title ?? 'Reviews'}
            </h2>

            {!!reviews.length && (
              <div className="relative col-start-2 row-start-1 col-span-2 lg:row-span-2 md:col-start-2 lg:col-span-1 flex flex-row items-center justify-end gap-4">
                <div className="flex flex-row gap-1">
                  {renderStarRating(scoreDisplay, true)}
                </div>

                <div className="relative flex items-center md:items-start">
                  <ScoreNumber score={scoreDisplay} />
                  <FontAwesomeIcon
                    className="md:text-[22px] rounded-full transform -translate-x-[10px] -translate-y-[22px] md:-translate-x-[15px] md:-translate-y-[6px] link-color"
                    icon={faCircleInfoSolid as IconProp}
                    onMouseEnter={handleHover}
                    onMouseLeave={handleLeave}
                  />
                </div>

                <div className={`bg-stone-50 border-[2px] border-blue-100 p-[10px] pb-[20px] rounded-[4px] self-end md:w-[340px] w-[250px]  shadow-lg absolute top-[80px] right-[10px] z-40 ${isHovered ? 'md:block' : 'hidden'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <FontAwesomeIcon
                      className="md:text-sm text-blue-400"
                      icon={faCircleInfo as IconProp}
                    />
                  </div>
                  <span className="relative z-10 font-normal font-body text-[14px] leading-[18px] text-black">
                    Een review is een persoonlijke ervaring van een consument. Alle
                    reviews samen leiden tot een rapportcijfer. Houd er rekening mee
                    dat niet alle consumenten hun ervaringen delen. Hierdoor is dit
                    rapportcijfer geen gegeven, maar een subjectieve waarde.
                  </span>
                </div>
              </div>
            )}

            {!reviews.length && (
              <div className="row-start-2 col-span-3 lg:col-span-2">
                <Message icon={faCircleInfo as IconProp} className="my-1">
                  {name} heeft nog geen reviews.
                </Message>
              </div>
            )}

            <div className={`${reviews.length > 0 ? 'row-start-2' : 'row-start-3'} col-span-3 lg:col-span-1 col-start-1 flex flex-col md:flex-row gap-2.5 lg:gap-4`}>
              <Link
                variant="primary"
                className="w-full md:w-auto"
                title="Schrijf een review"
                href={reviewUrl}
              >
                <FontAwesomeIcon icon={faPen as IconProp} />
                Schrijf een review
              </Link>

              {!!reviews.length && (
                <Link
                  variant="default"
                  className="w-full md:w-auto"
                  title="Bekijk alle reviews"
                  href={reviewIndexUrl}
                >
                  <span>Bekijk alle <span className="text-blue-700 campaign-accent-color font-black">{totalReviews}</span> reviews</span>
                  <FontAwesomeIcon icon={faChevronRight as IconProp} />
                </Link>
              )}
            </div>
          </div>
        </div>

        {!!reviews.length && (
          <>
            <hr className="border-t panel-separator-border-color" />
            <div className="mt-6 px-3 mb-3">
              <h3 className="panel-title--secondary">
                Meest recente reviews
              </h3>
            </div>

            <div className="relative overflow-hidden -mx-4 md:-mx-8">
              {!!startIndex && <SlideShowButton direction="prev" onClick={handlePrevious} />}
              <div className="grid grid-cols-1 md:grid-cols-3 mx-4 md:mx-12 items-center md:flex-row md:justify-start gap-2 md:gap-6 md:flex-wrap pb-10">
                {renderedReview.map((review) => (
                    <ReviewCard
                      scope={scope}
                      key={review.id}
                      review={review}
                      transformDate={true}
                    />
                ))}
              </div>
              {showNext && <SlideShowButton direction="next" onClick={handleNext} />}
            </div>
          </>
        )}
      </div>
    </Panel>
  );
};

export default ReviewBlock;
